import { type Theme, UnstyledButton } from '@backoffice-frontend/patterns'
import {
  isImpersonationSession,
  SESSION_PROLONG_BY_IN_MINUTES,
  useCountdown,
} from '@backoffice-frontend/temporary-backoffice-sessions-common'
import { Text } from '@moia-dev/pace-web'
import { Trans, useTranslation } from 'react-i18next'
import type { ActiveTemporarySession } from './types'
import { formatCountdown } from './utils'

const styles = {
  banner: (hasExpired: boolean) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    gap: theme.space.Space3,
    paddingTop: theme.space.Space1,
    paddingInline: theme.space.Space2,
    paddingBottom: theme.space.Space3,
    color: theme.semantic.ColorContentOnAccentDefault,
    backgroundColor: hasExpired
      ? theme.semantic.ColorCriticalDefault
      : theme.semantic.ColorAccentDefault,
  }),
  context: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.space.Space2,
    minWidth: 0,
  }),
  actions: (theme: Theme) => ({
    display: 'flex',
    gap: theme.space.Space3,
    alignItems: 'stretch',
  }),
  button: {
    fontWeight: 'var(--font-weight-bold)',
    color: 'inherit',
    '&:hover': {
      opacity: 0.8,
    },
  },
  divider: (theme: Theme) => ({
    width: '1px',
    background: theme.semantic.ColorContentOnAccentDefault,
  }),
}

type TemporarySessionBannerProps = {
  session: ActiveTemporarySession
  /**
   * Resolved display name of the organization a scoped-access session targets.
   * Shows the `organizationId` as a fallback.
   */
  organizationName?: string
  onProlong: () => void
  onEnd: () => void
  isProlonging?: boolean
  isEnding?: boolean
}

/**
 * Persistent, purely presentational indicator shown while an actor operates
 * inside a temporary Backoffice session, with a countdown to expiry and the
 * session actions.
 */
export const TemporarySessionBanner = ({
  session,
  organizationName,
  onProlong,
  onEnd,
  isProlonging = false,
  isEnding = false,
}: TemporarySessionBannerProps) => {
  const { t } = useTranslation()
  const secondsRemaining = useCountdown(session.expiresAt)
  const hasExpired = secondsRemaining <= 0

  const countdown = formatCountdown(secondsRemaining)
  const targetUserName = session.targetUserName ?? t('common_unknown_label')
  const targetUserLabel = session.targetUserEmail
    ? `${targetUserName} (${session.targetUserEmail})`
    : targetUserName

  return (
    <div css={styles.banner(hasExpired)}>
      <div css={styles.context}>
        <Text css={{ color: 'inherit' }}>
          {hasExpired ? (
            t('authenticatedAppView_temporarySession_expired_label')
          ) : isImpersonationSession(session) ? (
            <Trans
              i18nKey="authenticatedAppView_temporarySession_impersonation_label"
              values={{
                userName: targetUserLabel,
                countdown,
              }}
              components={{ b: <b /> }}
            />
          ) : (
            <Trans
              i18nKey="authenticatedAppView_temporarySession_scopedAccess_label"
              values={{
                organizationName:
                  organizationName ?? session.permissions.organizationId,
                countdown,
              }}
              components={{ b: <b /> }}
            />
          )}
        </Text>
      </div>

      <div css={styles.actions}>
        {/* An expired session can no longer be prolonged, only ended. */}
        {!hasExpired && (
          <>
            <UnstyledButton
              css={styles.button}
              disabled={isProlonging}
              onClick={onProlong}
            >
              {t('authenticatedAppView_temporarySession_prolong_button', {
                minutes: SESSION_PROLONG_BY_IN_MINUTES,
              })}
            </UnstyledButton>
            <div css={styles.divider} />
          </>
        )}

        <UnstyledButton css={styles.button} disabled={isEnding} onClick={onEnd}>
          {t('authenticatedAppView_temporarySession_end_button')}
        </UnstyledButton>
      </div>
    </div>
  )
}
